import React from "react";

import { MessageDescriptor } from "@formatjs/intl/src/types";
import cn from "classnames";
import { useIntl } from "react-intl";

import Email from "@mapmycustomers/shared/types/email/Email";
import EmailRecipientAddress from "@mapmycustomers/shared/types/email/EmailRecipientAddress";
import { isNotEmptyString } from "@mapmycustomers/shared/util/assert";
import { isEmptyString } from "@mapmycustomers/shared/util/stringUtils";

import getEmailDate from "@app/component/EntityEmails/util/getEmailDate";
import { formatDate } from "@app/util/formatters";

import styles from "./EmailInfo.module.scss";
import messages from "./messages";

interface EmailInfoProps {
  className?: string;
  email: Email;
}

const formatEmailRecipientAddress = ({ email, name }: EmailRecipientAddress) => {
  return isNotEmptyString(name) ? `${name} <${email}>` : email;
};

const formatEmailRecipientAddresses = (emailRecipientAddresses: EmailRecipientAddress[] | null) => {
  return emailRecipientAddresses
    ? emailRecipientAddresses.map(formatEmailRecipientAddress).join(", ")
    : null;
};

const EmailInfo: React.FC<EmailInfoProps> = ({ className, email }) => {
  const intl = useIntl();

  const from = formatEmailRecipientAddress(email.from[0]);
  const to = formatEmailRecipientAddresses(email.recipientAddress);
  const cc = formatEmailRecipientAddresses(email.cc);
  const bcc = formatEmailRecipientAddresses(email.bcc);
  const date = formatDate(getEmailDate(email), intl.formatMessage(messages.emailInfoDateFormat));

  const renderField = (titleMessage: MessageDescriptor, value: null | string) => {
    if (!value || isEmptyString(value)) {
      return null;
    }

    return (
      <>
        <div className={styles.fieldTitle}>{intl.formatMessage(titleMessage)}</div>
        <div className={styles.fieldValue}>{value}</div>
      </>
    );
  };

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.grid}>
        {renderField(messages.emailInfoFrom, from)}
        {renderField(messages.emailInfoTo, to)}
        {renderField(messages.emailInfoCc, cc)}
        {renderField(messages.emailInfoBcc, bcc)}
        {renderField(messages.emailInfoDate, date)}
      </div>
    </div>
  );
};

export default EmailInfo;
